import { CONTACT, SITE } from '@/constants/site.constants';

/**
 * Copy for the public account-deletion page.
 *
 * Google Play requires this page to be functional, to feature the deletion path prominently,
 * and to name the app as it appears on the store listing — hence SITE.name in the heading
 * rather than a generic "this service".
 *
 * The retention figure must match FINANCIAL_RETENTION_YEARS on the server. If one moves,
 * move the other: this page is a promise about what the code does — including the deletion
 * record, which the server snapshots so the retained financial rows stay attributable.
 */

export const DELETE_ACCOUNT = {
  eyebrow: 'Your data',
  title: `Delete your ${SITE.name} account`,
  summary: `Deleting is permanent. Enter the email address on your ${SITE.name} account and we will send a 4-digit confirmation code to it. Nothing is deleted until you enter that code.`,

  emailLabel: 'College email address',
  emailPlaceholder: 'you@college.ac.in',
  emailCta: 'Send confirmation code',

  codeHeading: 'Enter your confirmation code',
  codeIntro:
    'We have sent a 4-digit code to that address, if it has a Shoutt account. The code expires in 10 minutes.',
  codeLabel: 'Confirmation code',
  codePlaceholder: '0000',
  codeCta: 'Permanently delete my account',
  codeBack: 'Use a different email',

  doneHeading: 'Your account has been deleted',
  doneBody:
    'Your profile, listings, Shoutts and messages have been removed, and this email can no longer sign in to Shoutt.',

  removedHeading: 'What is deleted immediately',
  removed: [
    'Your name, profile photo, bio, phone number and college email are removed from your profile and from everything other students can see',
    'Your listings and Shoutts — they stop appearing on your campus',
    'The content of messages you sent',
    'Your notifications, saved devices and every signed-in session',
  ],

  keptHeading: 'What is kept, and why',
  kept: [
    'Rental and payment records, under an anonymised identifier that no longer carries your name, email or phone number.',
    'A restricted deletion record holding the name, email and phone number on the account, kept only so those retained transactions remain attributable — for example if a tax authority queries an invoice. It is visible to Shoutt administrators only, never to another student, and never used to contact you.',
    'We are required to retain the financial records under the Companies Act 2013 and Indian tax law. Both they and the deletion record are destroyed automatically at the end of the retention period.',
  ],

  blockedHeading: 'If you have a rental in progress',
  blockedBody:
    'An account with an active rental cannot be deleted — the other student is still waiting on an item or a deposit. Finish or cancel the rental first, then come back here.',

  helpHeading: 'Prefer to email us?',
  helpBody: `Write to ${CONTACT.email} from the address on your account and we will action the deletion for you.`,
  helpEmail: CONTACT.email,
} as const;

/** Kept in sync with FINANCIAL_RETENTION_YEARS in the server constants. */
export const RETENTION_YEARS = 8;
